import { getSavedCards, deleteStoredCard } from '@koala/sdk/v4';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useGlobalDispatch } from '@/redux/global/reducer';
import { useSelector } from '@/redux';
import { ERROR_MESSAGES } from '@/constants/events';
import { createHttpClient } from '@/services/client';
import { getOrigin } from '@/utils';
import * as ErrorReporter from '@/utils/errorReporter';
import { prepareErrorMessage } from '@/utils/global';

const STORED_CARDS_QUERY_KEY = 'stored-cards';

export const useStoredCards = (
  locationId?: number | null,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const userId = useSelector((state) => state.app.me.data?.id);
  const client = createHttpClient({
    origin: getOrigin(window.location.host),
  });
  return useQuery({
    queryKey: [STORED_CARDS_QUERY_KEY, userId, locationId ?? null],
    queryFn: () => getSavedCards({ client }, undefined, locationId ?? undefined),
    enabled: Boolean(userId) && enabled,
    refetchOnWindowFocus: false,
    // This query had no error reporting, so a Wallet fetch failure — including a
    // network-layer drop — was invisible in Sentry (TEAM-14780). Report on the
    // final failure so the connection-error UI added below is observable too.
    onError: (error) => {
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.FETCH_SAVED_CARDS_FAILURE,
      });
    },
  });
};

export const useDeleteStoredCard = () => {
  const queryClient = useQueryClient();
  const me = useSelector((root) => root.app.me.data);
  const { dispatch, displayErrorToast, displayToast } = useGlobalDispatch();
  const client = createHttpClient({
    origin: getOrigin(window.location.host),
  });

  return useMutation((cardId: string) => deleteStoredCard(cardId, { client }), {
    onSuccess: () => {
      queryClient.invalidateQueries([STORED_CARDS_QUERY_KEY, me?.id]);
      dispatch(displayToast('Card removed successfully.'));
    },
    onError: async (error) => {
      ErrorReporter.captureException(error);
      const errorMessage = await prepareErrorMessage(
        'Failed to remove card. Please try again.',
        error,
      );
      dispatch(displayErrorToast(errorMessage.message));
    },
  });
};
